import {
  Box,
  Button,
  Menu,
  MenuItem,
  Skeleton,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  BulkSendIcon,
  MeAndOthersIcon,
  OnlySignerIcon,
} from "global/components/RequestTypeIcons";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import { useFeature } from "hooks/use-feature";
import { usePopover } from "hooks/use-popover";
import { hasAccessTo, hasWorkspacePermission } from "layouts/Config";
import { DateRange, SignRequest, SortingConfig } from "models/interfaces";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import {
  setLoading,
  setReduxSelectedParticipant,
} from "redux/appSlice";
import { selectEmail, selectWorkspacePermissions } from "redux/authSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { downloadFilesAsZip } from "screens/SignRequest/ParticipantScreen/ParticipantScreenService";
import { addSigner } from "screens/SignRequest/SignRequestHelper";
import history from "utils/history";
import { RootState } from "utils/store";
import { useTitle } from "utils/UseTitle";
import { PlusCircleGlyph } from "../components/SignRequestsIcons";
import { handleSignNow } from "../MySignRequestHelper";
import {
  fetchInboxRequests,
  fetchInboxRequestsCount,
  getSignRequestDataByToken,
} from "../MySignRequestService";
import inboxStyles from "./Inbox.styles";
import inboxTokens, { inboxResponsiveTokens } from "./Inbox.tokens";
import InboxFilterTabs, { INBOX_TABS } from "./InboxFilterTabs";
import InboxPagination from "./InboxPagination";
import InboxRequestRow from "./InboxRequestRow";
import {
  getInboxRowState,
  INBOX_TAB_QUERY,
  InboxRowAction,
  InboxRowState,
  InboxTabKey,
  isServerPagedTab,
} from "./InboxRowHelper";
import InboxToolbar from "./InboxToolbar";
import EmptyState from "global/components/EmptyState/EmptyState";
import { tourAttr } from "tour";

const styles = inboxStyles;
const { primaryButton } = inboxTokens;
const { phone } = inboxResponsiveTokens;

/** Rows per page, matching the inbox list this screen replaces. */
const PAGE_SIZE = 10;

/**
 * Backstop on the gather-every-page path, which only the three tabs the API
 * cannot filter ever take. At the API's page size this is 500 rows.
 */
const MAX_PAGES = 50;

const SORTING_CONFIGS: SortingConfig[] = [
  { entity: "signRequest", order: "DESC", field: "createdOn" },
];

const EMPTY_RANGE: DateRange = { fromDate: null, toDate: null };

/** `createdOn` is a string, so a merged bucket needs a real comparison. */
const CREATED_ON_FORMAT = "MM/DD/YYYY HH:mm:ss";

const byNewestFirst = (a: SignRequest, b: SignRequest) =>
  moment(b.createdOn, CREATED_ON_FORMAT).valueOf() -
  moment(a.createdOn, CREATED_ON_FORMAT).valueOf();

interface InboxData {
  /**
   * Exactly one page for a server-paged tab; the whole bucket for the three
   * tabs paged in the browser. `serverPaged` says which.
   */
  rows: SignRequest[];
  total: number;
  serverPaged: boolean;
}

const EMPTY_DATA: InboxData = { rows: [], total: 0, serverPaged: true };

/**
 * Inbox — requests other people have sent the signed-in user.
 *
 * "All requests" and "Completed" each map onto a single inbox status, so they
 * ask the API for the selected page and nothing else. The other three cannot be
 * expressed as a server filter — "Action needed" and "In progress" are the two
 * halves of `Pending` split by `currentSigners`, and "Closed" spans three
 * statuses the API filters one at a time — so those gather their bucket once
 * and page it locally. See the Backend Requests note for the endpoints that
 * would make all five uniform.
 */
const InboxView = () => {
  useTitle(title.INBOX);
  /**
   * The phone reference drops the `<h1>` from the page body and carries the
   * screen name in the app bar instead. The tablet reference keeps it on the
   * page, so the app bar is only fed below `sm`.
   */
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  useSetPageHeader(isPhone ? "Inbox" : undefined);
  const dispatch = useAppDispatch();
  const userEmail: string = useAppSelector(selectEmail);
  const { userName } = useAppSelector((state: RootState) => state.auth);
  // Subscribe so hasWorkspacePermission()-gated actions re-render when the async
  // fetchMyWorkspacePermissions() resolves after mount (otherwise they stay fail-open).
  useAppSelector(selectWorkspacePermissions);
  const canBulkSend = useFeature(FeatureKeys.ENVELOPE_BULK_SEND);
  /** Backs the phone's square Create button — the split button's stand-in. */
  const createMenu = usePopover<HTMLElement>();

  const [data, setData] = useState<InboxData>(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRange>(EMPTY_RANGE);
  const [openDateRangePicker, setOpenDateRangePicker] =
    useState<boolean>(false);
  const [tab, setTab] = useState<InboxTabKey>("all");
  const [page, setPage] = useState<number>(1);

  // Rapid typing fires overlapping fetches; only the newest one may commit.
  const requestIdRef = useRef<number>(0);
  // The mount fetch must not sit behind the search debounce.
  const isFirstLoadRef = useRef<boolean>(true);

  const canCreate =
    hasAccessTo(strings.resources.sign, strings.addPermission) &&
    hasWorkspacePermission("document.create");
  const canDownload = hasAccessTo(
    strings.resources.sign,
    strings.downloadPermission,
  );

  /** Walks one status to the end. Only the locally-paged tabs reach this. */
  const fetchEveryPage = async (
    status: string,
    search: string,
    range: DateRange,
  ): Promise<SignRequest[]> => {
    const first = await fetchInboxRequests(
      1,
      SORTING_CONFIGS,
      status,
      search,
      range,
    );
    const collected: SignRequest[] = isTruthy(first) ? [...first] : [];
    const total =
      Number(await fetchInboxRequestsCount(status, search, range)) || 0;

    let pageNumber = 2;
    while (collected.length < total && pageNumber <= MAX_PAGES) {
      const batch = await fetchInboxRequests(
        pageNumber,
        SORTING_CONFIGS,
        status,
        search,
        range,
      );
      if (!isTruthy(batch) || batch.length === 0) break;
      collected.push(...batch);
      pageNumber += 1;
    }
    return collected;
  };

  /**
   * `reuseTotal` is set when only the page changed: the bucket's size can't
   * have moved, so the count call is skipped and paging costs one request.
   */
  const load = async (
    tabKey: InboxTabKey,
    pageNumber: number,
    search: string,
    range: DateRange,
    reuseTotal: boolean = false,
  ) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    try {
      const query = INBOX_TAB_QUERY[tabKey];

      if (isServerPagedTab(tabKey)) {
        const status = query.statuses[0];
        const [list, count] = await Promise.all([
          fetchInboxRequests(
            pageNumber,
            SORTING_CONFIGS,
            status,
            search,
            range,
          ),
          reuseTotal
            ? Promise.resolve(null)
            : fetchInboxRequestsCount(status, search, range),
        ]);
        if (requestIdRef.current !== requestId) return;
        setData((previous) => ({
          rows: isTruthy(list) ? list : [],
          total: reuseTotal ? previous.total : Number(count) || 0,
          serverPaged: true,
        }));
        return;
      }

      const batches = await Promise.all(
        query.statuses.map((status) => fetchEveryPage(status, search, range)),
      );
      if (requestIdRef.current !== requestId) return;

      const merged = batches.reduce<SignRequest[]>(
        (acc, batch) => acc.concat(batch),
        [],
      );
      const rows = query.needsRowFilter
        ? merged.filter(
            (request) => getInboxRowState(request, userEmail).tab === tabKey,
          )
        : merged;
      // One status comes back already ordered; a merge of three does not.
      if (query.statuses.length > 1) rows.sort(byNewestFirst);

      setData({ rows, total: rows.length, serverPaged: false });
    } catch (error: any) {
      if (requestIdRef.current !== requestId) return;
      setData(EMPTY_DATA);
      handleErrorMessage(error);
    } finally {
      if (requestIdRef.current === requestId) setIsLoading(false);
    }
  };

  useEffect(() => {
    const trimmed = searchText.trim();
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      load(tab, 1, trimmed, dateRange);
      return;
    }
    const timer = setTimeout(
      () => load(tab, 1, trimmed, dateRange),
      strings.SEARCH_TIME_OUT,
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  // A server-paged tab already holds exactly the rows for `currentPage`.
  const pageRows = data.serverPaged
    ? data.rows
    : data.rows.slice(start, start + PAGE_SIZE);

  const hasFilters =
    !!searchText.trim() ||
    !!dateRange.fromDate ||
    !!dateRange.toDate ||
    tab !== "all";
  const isFresh = !isLoading && data.total === 0 && !hasFilters;
  const isEmptyResult = !isLoading && data.total === 0 && !isFresh;

  const handleTabChange = (value: InboxTabKey) => {
    setTab(value);
    setPage(1);
    load(value, 1, searchText.trim(), dateRange);
  };

  const handlePageChange = (value: number) => {
    setPage(value);
    // A locally-paged tab already holds every row, so it just re-slices.
    if (isServerPagedTab(tab)) {
      load(tab, value, searchText.trim(), dateRange, true);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handleDateApplied = (range?: DateRange) => {
    setPage(1);
    load(tab, 1, searchText.trim(), range ?? dateRange);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setDateRange(EMPTY_RANGE);
    setTab("all");
    setPage(1);
    load("all", 1, "", EMPTY_RANGE);
  };

  // Same request-type routing the sidebar and the Sent header use: "signer"
  // seeds the signed-in user as the only participant and jumps straight to the
  // self-sign flow, every other type goes through the create wizard.
  const handleRequestTypeSelect = (type: string) => {
    if (type === "signer") {
      const selfSigner = {
        id: 0,
        name: userName,
        email: userEmail,
        order: 0,
        color: "",
        cc: false,
      };
      addSigner(selfSigner, [], dispatch, false);
      dispatch(setReduxSelectedParticipant(selfSigner));
      history.push(viewpaths.createSelfSignRequestPath);
      return;
    }

    history.push(`${viewpaths.createSignRequestsViewPath}?type=${type}`);
  };

  const handleDownload = async (request: SignRequest) => {
    try {
      dispatch(setLoading(true));
      const detail = await getSignRequestDataByToken(request.requestToken);
      downloadFilesAsZip(detail, (value: boolean) => {
        dispatch(setLoading(value));
      });
    } catch (error: any) {
      dispatch(setLoading(false));
      handleErrorMessage(error);
    }
  };

  const handleRowAction = (action: InboxRowAction, request: SignRequest) => {
    switch (action) {
      case "sign":
        handleSignNow(request.requestToken);
        return;
      case "download":
        handleDownload(request);
        return;
    }
  };

  /** Drops the row's button when the user's role can't perform it. */
  const applyPermissions = (state: InboxRowState): InboxRowState => {
    if (state.action === "download" && !canDownload) {
      return { ...state, action: undefined };
    }
    return state;
  };

  // Mirrors `InboxRequestRow`'s two halves so the placeholder stacks into cards
  // on a phone and stays one line from `sm` up, exactly as the real rows do.
  const renderSkeletons = () => (
    <Box aria-hidden="true" sx={styles.listCard}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Box key={`inbox-skeleton-${index}`} sx={styles.skeletonRow}>
          <Box sx={styles.rowMain}>
            <Skeleton
              variant="rounded"
              animation="wave"
              sx={styles.skeletonTile}
            />
            <Box sx={styles.rowText}>
              <Skeleton
                variant="text"
                animation="wave"
                width="45%"
                height={19}
              />
              <Skeleton
                variant="text"
                animation="wave"
                width="28%"
                height={17}
              />
            </Box>
          </Box>
          <Box sx={styles.rowMeta}>
            <Skeleton
              variant="rounded"
              animation="wave"
              width={132}
              height={27}
              sx={{ borderRadius: "999px", flexShrink: 0 }}
            />
            <Box sx={styles.actionSlot}>
              <Skeleton
                variant="rounded"
                animation="wave"
                width={102}
                height={37}
                sx={{ borderRadius: "9px" }}
              />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderFreshState = () => (
    // No wrapper card: the block draws its own border (`framed`), so an empty
    // inbox reads as one box rather than a tinted panel inside an empty card.
    <EmptyState
      framed
      icon="inbox"
      title="Your inbox is empty"
      body={
        <>
          Documents that others send you to review, approve or sign will land
          here. You&apos;ll also get an email notification when something
          arrives.
          <Box component="div" sx={styles.freshHint}>
            Waiting on someone? Ask them to send the request to{" "}
            <Box component="span" sx={styles.freshHintStrong}>
              {userEmail}
            </Box>
          </Box>
        </>
      }
    />
  );

  const renderNoResults = () => {
    const query = searchText.trim();
    const activeTab = INBOX_TABS.find((entry) => entry.key === tab);
    return (
      // Always a filtered empty, never a first use — hence the magnifier, no
      // "yet", and a way back rather than a create action.
      <EmptyState
        icon="search"
        title="No requests match"
        body={
          query
            ? `Nothing in your inbox matches "${query}". Check the spelling or try a document name, sender, or status.`
            : `You have no requests under "${activeTab?.label}". Try a different filter or clear it to see all requests.`
        }
        primaryAction={{
          label: "Clear search & filters",
          onClick: handleClearFilters,
        }}
      />
    );
  };

  const renderList = () => (
    <>
      <Box {...tourAttr("inbox.list")} sx={styles.listCard}>
        {pageRows.map((request, index) => (
          <InboxRequestRow
            key={request.id ?? `inbox-row-${index}`}
            request={request}
            state={applyPermissions(getInboxRowState(request, userEmail))}
            disabled={isLoading}
            onAction={handleRowAction}
          />
        ))}
      </Box>
      <InboxPagination
        page={currentPage}
        totalPages={totalPages}
        range={[start + 1, start + pageRows.length]}
        total={data.total}
        label={data.total === 1 ? "request" : "requests"}
        onChange={handlePageChange}
      />
    </>
  );

  /**
   * The three request types, shared by the split button's caret menu and the
   * phone's square Create button so neither can drift from the other.
   */
  const requestTypeMenuItems = [
    {
      icon: <OnlySignerIcon sx={{ fontSize: 20, color: "text.secondary" }} />,
      label: "I am the only signer",
      onClick: () => handleRequestTypeSelect("signer"),
    },
    {
      icon: <MeAndOthersIcon sx={{ fontSize: 20, color: "text.secondary" }} />,
      label: "Me and others",
      onClick: () => handleRequestTypeSelect("others"),
    },
    {
      icon: (
        <BulkSendIcon
          sx={{
            fontSize: 20,
            color: canBulkSend ? "text.secondary" : "text.disabled",
          }}
        />
      ),
      label: "Bulk send",
      disabled: !canBulkSend,
      onClick: () => {
        if (!canBulkSend) return;
        history.push(viewpaths.bulkSendViewPath);
      },
    },
  ];

  return (
    <Box sx={styles.page}>
      <Box sx={styles.header}>
        <Box sx={styles.headerText}>
          <Typography component="h1" sx={styles.headerTitle}>
            Inbox
          </Typography>
          <Typography component="p" sx={styles.headerSubtitle}>
            Documents others have sent you to review and sign.
          </Typography>
        </Box>
        {canCreate && !isFresh && (
          <Box sx={styles.headerActions}>
            <CustomDropdownButton
              handleMainClick={() => handleRequestTypeSelect("others")}
              buttonLabel="Create Request"
              startIcon={
                <PlusCircleGlyph
                  sx={{
                    width: primaryButton.iconSize,
                    height: primaryButton.iconSize,
                  }}
                />
              }
              menuItems={requestTypeMenuItems}
              disabled={isLoading}
            />
          </Box>
        )}
      </Box>

      {/*
        Phone "Create Request" — full width under the header, opening the same
        request-type chooser as the split button's caret.
      */}
      {canCreate && !isFresh && (
        <Button
          disableRipple
          disabled={isLoading}
          aria-haspopup="menu"
          aria-expanded={createMenu.open}
          sx={styles.createMobile}
          onClick={(event) => {
            createMenu.anchorRef.current = event.currentTarget;
            createMenu.handleOpen();
          }}
        >
          <PlusCircleGlyph
            sx={{
              width: phone.createButton.iconSize,
              height: phone.createButton.iconSize,
            }}
          />
          Create Request
        </Button>
      )}

      {/* Request-type chooser behind the phone's Create button. */}
      {canCreate && !isFresh && (
        <Menu
          anchorEl={createMenu.anchorRef.current}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          open={createMenu.open}
          onClose={createMenu.handleClose}
        >
          {requestTypeMenuItems.map((item) => (
            <MenuItem
              key={item.label}
              disabled={item.disabled}
              onClick={() => {
                createMenu.handleClose();
                item.onClick();
              }}
            >
              <Box
                component="span"
                sx={{ display: "flex", alignItems: "center", mr: 1.25 }}
              >
                {item.icon}
              </Box>
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}

      {isFresh ? (
        renderFreshState()
      ) : (
        <>
          <InboxToolbar
            searchText={searchText}
            onSearchChange={handleSearchChange}
            dateRange={dateRange}
            setDateRange={setDateRange}
            openDateRangePicker={openDateRangePicker}
            setOpenDateRangePicker={setOpenDateRangePicker}
            onDateApplied={handleDateApplied}
            disabled={isLoading}
          />
          <InboxFilterTabs
            value={tab}
            disabled={isLoading}
            onChange={handleTabChange}
          />
          <Box aria-busy={isLoading || undefined}>
            {isLoading && renderSkeletons()}
            {isEmptyResult && renderNoResults()}
            {!isLoading && !isEmptyResult && renderList()}
          </Box>
        </>
      )}
    </Box>
  );
};

export default InboxView;
